import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { apiFetch } from '@/domains/general/api/client';
import { paginationParams } from '@/domains/general/api/constants';

import { criteriaKeys, perspectiveKeys } from './keys';
import type { CriteriaListResponse, PerspectiveListResponse } from './types';

export const criteriaQueries = {
  list: (applicationId: string, perspectiveId?: string) =>
    queryOptions({
      queryKey: criteriaKeys.list(applicationId, perspectiveId),
      queryFn: (): Promise<CriteriaListResponse> => {
        const params = new URLSearchParams();
        params.set('applicationId', applicationId);
        if (perspectiveId) params.set('perspectiveId', perspectiveId);
        // Geen pagination: alle criteria van het perspectief horen in het paneel.
        return apiFetch(`/criteria?${params}`);
      },
      enabled: !!applicationId,
    }),
};

export const perspectiveQueries = {
  list: () =>
    queryOptions({
      queryKey: perspectiveKeys.list(),
      queryFn: (): Promise<PerspectiveListResponse> =>
        apiFetch(`/perspectives?${paginationParams()}`),
    }),
};

export function useCriteria(applicationId: string, options?: { perspectiveId?: string }) {
  const criteriaQuery = useQuery(criteriaQueries.list(applicationId, options?.perspectiveId));
  const perspectivesQuery = useQuery(perspectiveQueries.list());

  useEffect(() => {
    const criteria = criteriaQuery.data?.items;
    const perspectives = perspectivesQuery.data?.items;
    if (!criteria || !perspectives) return;

    const allowed = new Set(perspectives.map((p) => p.id));
    const unexpected = criteria.filter((c) => !c.perspectiveIds.some((id) => allowed.has(id)));
    if (unexpected.length === 0) return;

    console.warn(
      "[criteria-auth] response contains criteria outside the user's perspectives",
      {
        unexpected: unexpected.map((c) => ({
          id: c.id,
          code: c.code,
          perspectiveIds: c.perspectiveIds,
        })),
        allowedPerspectiveIds: [...allowed],
      },
    );
  }, [criteriaQuery.data, perspectivesQuery.data]);

  return criteriaQuery;
}

export function usePerspectives() {
  return useQuery(perspectiveQueries.list());
}
